<template>
  <div>
    <es-loader
      v-if="loading"
      style="margin: 6.5rem"
    />
    <es-exception v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <router-view
        v-if="hasModule('PRSID:PRPRF:PROFILE:VIEW') || hasModule('DRMCH:PRPRF:PROFILE:VIEW')"
        name="personProfileHeader"
        :dmNearMatchTotalCount="dmNearMatchTotalCount"
        :historyRecordsCount="historyRecordsCount"
        :isRetired="isRetired"
        :mergedRecordsCount="mergedRecordsCount"
        :person="person"
        :personTypeNames="personTypeNames"
        :pidNearMatchTotalCount="pidNearMatchTotalCount"
      />
      <router-view
        v-if="hasModule('PRSID:PRPRF:PROFILE:VIEW') || hasModule('DRMCH:PRPRF:PROFILE:VIEW')"
        name="personProfileBody"
        @emitFurther="storeReason($event)"
        :aliasIds="aliasIds"
        :dmNearMatches="dmNearMatches"
        :dmNearMatchTotalCount="dmNearMatchTotalCount"
        :isRetired="isRetired"
        :person="person"
        :pidNearMatches="pidNearMatches"
        :pidNearMatchTotalCount="pidNearMatchTotalCount"
        :reason="reason"
        :recentHistoryRecords="recentHistoryRecords"
        :userHasAccessToPersonLocation="userHasAccessToPersonLocation"
      />
      <router-view
        v-if="hasModule('PRSID:PRPRF:NOTES:ADD:ADMIN')
          || hasModule('PRSID:PRPRF:NOTES:ADD:GENERAL')"
        name="personProfileSubBody"
        :isRetired="isRetired"
        :person="person"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  components: {
    EsException,
    EsLoader,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  mixins: [
    BreadcrumbsManager,
    NotificationManager,
    PermissionManager,
  ],
  data() {
    return {
      aliasIds: [],
      dmNearMatches: [],
      dmNearMatchTotalCount: null,
      historyRecordsCount: null,
      loading: false,
      loadingError: false,
      matchDetails: [],
      mergedRecordsCount: null,
      notes: [],
      person: null,
      personTypeNames: [],
      pidNearMatches: [],
      pidNearMatchTotalCount: null,
      reason: null,
      recentHistoryRecords: [],
    };
  },
  created() {
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'pidDashboard' } },
      { text: 'Person Information', to: {} },
    ]);
    this.onLoad();
  },
  computed: {
    isRetired() {
      return (this.$route.query.retired === true) || (this.$route.query.retired === 'true');
    },
    userHasAccessToPersonLocation() {
      const { locationPermissions } = this.$store.state.user;

      // If there are no location permissions, means it has all districts and all schools access
      if (!locationPermissions.length) return true;

      // Collect unique district and school codes, avoiding shadowing issues
      const districts = [...new Set(locationPermissions
        .flatMap(({ districts: d }) => d.map(({ code }) => code)))];
      const schools = [...new Set(locationPermissions
        .flatMap(({ schools: s }) => s.map(({ code }) => code)))];

      const { districtCode, schoolCode } = this.person;

      const hasDistrictAccess = !districts.length || districts.includes(districtCode);
      const hasSchoolAccess = !schools.length || schools.includes(schoolCode);

      return hasDistrictAccess && hasSchoolAccess;
    },
  },
  mounted() {
    this.$root.$on('reloadPerson', this.onLoad);
  },
  methods: {
    ...mapActions('lookups', [
      'loadCertificateTypes',
      'loadEthnicSubGroups',
      'loadEthnicities',
      'loadGenders',
      'loadGrades',
      'loadHighestDegrees',
      'loadItinerantFlags',
      'loadJobAssignments',
      'loadPersonTypes',
      'loadPidSchoolYears',
      'loadPrograms',
      'loadRaces',
      'loadResidencyStatuses',
      'loadSources',
      'loadStates',
      'loadSubjectMatterAreas',
      'loadTitles',
    ]),
    getAliasIds() {
      if (!this.hasModule('PRSID:PRPRF:PROFILE:VIEW')) return null;
      const sort = [{
        field: 'aliasId',
        order: 'ASC',
      }];
      const params = new URLSearchParams();
      params.append('sort', JSON.stringify(sort));
      if (this.isRetired) {
        return axios.get(`retired-person/${this.id}/aliasid`, { params });
      }
      return axios.get(`person/${this.id}/aliasid`, { params });
    },
    getDmPendingNearMatches() {
      if (!this.hasModule('DRMCH:MATCH:OPTIONS:VIEW')) return null;
      if (!this.isRetired) {
        return axios.get(`person/${this.id}/dmtrans?open=true&pageSize=6`);
      }
      return null;
    },
    async getDmTransactionDetails() {
      const promises = [];
      this.dmNearMatches.forEach((match) => {
        promises.push(axios.get(`directmatch/transactions/${match.id}/details`));
      });
      const response = await Promise.all(promises);

      for (let i = 0; i < this.dmNearMatches.size; i += 1) {
        this.dmNearMatches[i].details = response[i].data;
      }
    },
    async getMergedRecords() {
      if (!this.hasModule('PRSID:PRPRF:PROFILE:VIEW')) return null;

      try {
        // Call both APIs
        const [retiredResponse, mergedResponse] = await Promise.all([
          axios.get(`person/${this.id}/associated`),
          axios.get(`person/${this.id}/merged`),
        ]);

        // Extract total counts from responses
        const retiredTotalCount = retiredResponse.data.totalCount || 0;
        const mergedTotalCount = mergedResponse.status === 204 ? 0 : mergedResponse.data.totalCount;

        // Calculate combined total count
        const totalCount = retiredTotalCount + mergedTotalCount;

        // Return combined total count
        return totalCount;
      } catch (e) {
        console.error('Error fetching associated records:', e);
        return 0;
      }
    },
    getPidPendingNearMatches() {
      if (!this.hasModule('PRSID:MATCH:OPTIONS:VIEW')) return null;
      if (!this.isRetired) {
        const params = new URLSearchParams();
        params.append('pageSize', '6');
        return axios.get(`person/${this.id}/pidtrans`, { params });
      }
      return null;
    },
    getRecentHistoryRecords() {
      if (!this.hasModule('PRSID:PRPRF:PROFILE:VIEW')) return null;
      const sort = [{
        field: 'lastUpdateDate',
        order: 'DESC',
      }];
      const params = new URLSearchParams();
      params.append('sort', JSON.stringify(sort));
      if (this.isRetired) {
        return axios.get(`retired-person/${this.id}/history`, { params });
      }
      return axios.get(`person/${this.id}/history`, { params });
    },
    loadPerson() {
      if (this.isRetired) {
        return axios.get(`retired-person/${this.id}/profile`);
      }
      return axios.get(`person/${this.id}/profile`);
    },
    loadPersonLocations() {
      if (this.isRetired) {
        return axios.get(`retired-person/${this.id}/location`);
      }
      return axios.get(`person/${this.id}/location`);
    },
    async onLoad() {
      try {
        this.loading = true;
        const responses = await Promise.all([
          this.getAliasIds(),
          this.getDmPendingNearMatches(),
          this.getMergedRecords(),
          this.getPidPendingNearMatches(),
          this.getRecentHistoryRecords(),
          this.loadCertificateTypes(),
          this.loadEthnicSubGroups(),
          this.loadEthnicities(),
          this.loadGenders(),
          this.loadGrades(),
          this.loadHighestDegrees(),
          this.loadItinerantFlags(),
          this.loadJobAssignments(),
          this.loadPerson(),
          this.loadPersonLocations(),
          this.loadPersonTypes(),
          this.loadPidSchoolYears(),
          this.loadPrograms(),
          this.loadRaces(),
          this.loadResidencyStatuses(),
          this.loadSources(),
          this.loadStates(),
          this.loadSubjectMatterAreas(),
          this.loadTitles(),
        ]);
        this.aliasIds = responses[0] ? responses[0].data.data : [];
        if (!this.isRetired) {
          this.dmNearMatches = responses[1] ? responses[1].data.data : [];
          this.dmNearMatchTotalCount = responses[1] ? responses[1].data.totalCount : 0;
          this.pidNearMatches = responses[3] ? responses[3].data.data : [];
          this.pidNearMatchTotalCount = responses[3] ? responses[3].data.totalCount : 0;
        }
        this.recentHistoryRecords = responses[4] ? responses[4].data.data : [];
        this.historyRecordsCount = responses[4] ? responses[4].data.totalCount : 0;
        [, , this.mergedRecordsCount] = responses;
        this.person = responses[13].data;
        if (this.person === null || this.person === '') {
          this.loadingError = true;
          this.showNotification('danger', ['Person not found']);
        }
        this.personTypeNames = [...new Set(responses[14].data.map(a => a.personTypeName))];
      } catch (e) {
        this.showNotification('danger', [e.response.data.message]);
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    storeReason(r) {
      this.reason = r;
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
